import React from "react";
import { Box, Typography, Button, Paper, Collapse } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ShippingProvidersButton from "../shipping/ShippingProvidersButton";
import { useTheme, alpha } from "@mui/material/styles";

// Percentage of the monthly order-dispatch quota at which we start warning the
// user proactively (before they hit the hard block).
const WARN_THRESHOLD_PCT = 80;

const DispatchControls = ({
  ordersToDispatch,
  onClearSelection,
  onDispatch,
  orderUsage,
}) => {
  const theme = useTheme();

  const hasOrdersToDispatch = ordersToDispatch && ordersToDispatch.length > 0;
  // Show providers button only for single orders (external providers typically handle one order at a time)
  const showProvidersButton = ordersToDispatch.length === 1;

  // Proactive quota warning: only when the plan defines a finite limit.
  const selectedCount = ordersToDispatch?.length || 0;
  const hasFiniteLimit =
    orderUsage && !orderUsage.unlimited && orderUsage.limit > 0;
  const remaining = hasFiniteLimit
    ? Math.max(orderUsage.limit - orderUsage.current, 0)
    : null;
  // This batch would exceed the monthly limit.
  const batchExceeds = hasFiniteLimit && selectedCount > remaining;
  // Approaching the limit (>= threshold) but not yet exceeded by this batch.
  const approaching =
    hasFiniteLimit &&
    !batchExceeds &&
    (orderUsage.percentage || 0) >= WARN_THRESHOLD_PCT;
  const showQuotaWarning = batchExceeds || approaching;

  return (
    <Collapse in={hasOrdersToDispatch}>
        {showQuotaWarning && (
            <Box
                sx={{
                    p: 1,
                    mb: 1,
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: alpha(
                        batchExceeds
                            ? theme.palette.error.main
                            : theme.palette.warning.main,
                        0.12
                    ),
                    color: batchExceeds
                        ? theme.palette.error.main
                        : theme.palette.warning.dark,
                }}
            >
                <WarningAmberIcon fontSize="small" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {batchExceeds
                        ? `Este despacho ultrapassa o limite do seu plano. Restam ${remaining} de ${orderUsage.limit} pedidos neste mês. Faça upgrade para despachar todos.`
                        : `Você está próximo do limite do seu plano: ${orderUsage.current}/${orderUsage.limit} pedidos despachados este mês (restam ${remaining}).`}
                </Typography>
            </Box>
        )}
        <Paper
            elevation={0}
            variant="outlined"
            sx={{
                p: 1.5,
                mb: 2,
                borderRadius: "6px",
                bgcolor: "background.paper",
                borderColor: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                <Box 
                    sx={{ 
                        bgcolor: theme.palette.primary.main, 
                        color: "white", 
                        borderRadius: "50%", 
                        width: 24, 
                        height: 24, 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: "bold"
                    }}
                >
                    {ordersToDispatch.length}
                </Box>
                <Typography variant="body2" fontWeight="500">
                    Selecionados
                </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                    onClick={onClearSelection}
                    startIcon={<CloseIcon />}
                    size="small"
                    color="inherit"
                >
                    Limpar
                </Button>

                {showProvidersButton && (
                    <ShippingProvidersButton
                        onClick={() => onDispatch(true)}
                        variant="outlined"
                        size="small"
                    >
                        Externo
                    </ShippingProvidersButton>
                )}

                <Button
                    onClick={() => onDispatch(false)}
                    endIcon={<SendIcon />}
                    variant="contained"
                    size="small"
                    color="primary"
                    sx={{ px: 3 }}
                >
                    Despachar
                </Button>
            </Box>
        </Paper>
    </Collapse>
  );
};

export default DispatchControls;
